/* eslint-disable no-eval */
/* eslint-disable max-classes-per-file */
import { AxiosRequestConfig, Method } from 'axios';

interface APIParam {
  [key: string]: string | number;
}

interface ParamsBuilder {
  addQueryParams: (pathParams: APIParam) => ParamsBuilder;
  addPathParams: (pathParams: APIParam) => ParamsBuilder;
  addRequestBody: (requestBody: object) => ParamsBuilder;
  addHeaders: (headers: APIParam) => ParamsBuilder;
  build(): APIParamsBuilderHelper;
  config: AxiosRequestConfig;
}

/**
 * @class APIParamsBuilder
 * @description Helps to build the AxiosRequestConfig object incrementally.
 */
class APIParamsBuilderHelper {
  /**
   * @field config
   * @description An axios config object which enables the user to pass url, method, data, params etc to an axios request.
   */
  private config: AxiosRequestConfig;

  constructor(config: AxiosRequestConfig) {
    this.config = config;
  }

  /**
   * @method config
   * @description Returns the config object that was built.
   */
  public get getConfig(): AxiosRequestConfig {
    return this.config;
  }

  /**
   * @method ParamsBuilder
   * @description Returns the actual ParamsBuilder class which will help to build the APIParamsBuilderHelper class.
   */

  /**
   * @class ParamsBuilder
   * @description Inner class that will create an object of the outer.
   */
  static ParamsBuilder = class {
    config: AxiosRequestConfig = {};

    constructor(path: string, method: Method) {
      // To create a config object, providing API path and API method is mandatory.
      this.config.url = path;
      this.config.method = method;
    }

    /**
     * @method addQueryParams
     * @description Adds query params to the config object
     * @param queryParams
     */
    addQueryParams(queryParams: APIParam): ParamsBuilder {
      this.config.params = queryParams;
      return this;
    }

    /**
     * @method addPathParams
     * @description Adds path params to the config object
     * @param pathParams
     */
    addPathParams(pathParams: APIParam): ParamsBuilder {
      Object.entries(pathParams).forEach(([key, value]) => {
        // eslint-disable-next-line no-param-reassign
        pathParams[key] = encodeURIComponent(value);
      });
      const apiPathTemplateString = eval(`\`${this.config.url}\``);
      this.config.url = apiPathTemplateString;
      return this;
    }

    /**
     * @method addRequestBody
     * @description Adds data to be sent with the request. Required for PUT and POST.
     * @param requestBody
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    addRequestBody(requestBody: any): ParamsBuilder {
      this.config.data = requestBody;
      return this;
    }

    /**
     * @method addHeaders
     * @description Adds header information to be sent with the request.
     * @param headers
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    addHeaders(headers: any): ParamsBuilder {
      this.config.headers = headers;
      return this;
    }

    /**
     * @method build
     * @description Builds the final object.
     * @returns Returns the final built object.
     */
    build(): APIParamsBuilderHelper {
      return new APIParamsBuilderHelper(this.config);
    }
  };
}

export default APIParamsBuilderHelper;
